import { IssueFieldEnum } from "@mcapps/shared/enums";
import { Card, CloseButton, Flex, Group, SegmentedControl, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import React, { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { ISSUE_STATES_OPTIONS } from "../issueConstants";
import { FilterDropdown } from "../IssuesTab/filters/FilterDropdown";
import {
  selectMultiRegisterIssuesAssignedToFilter,
  selectMultiRegisterIssuesAssignedToFilterOptions,
  selectMultiRegisterIssuesFreeTextFilter,
  selectMultiRegisterIssuesStatusFilter,
  selectMultiRegisterIssuesTaskOrIssueFilter,
} from "./multiRegisterIssuesSelectors";
import {
  setMultiRegisterIssuesAssignedToFilterInRedux,
  setMultiRegisterIssuesFreeTextFilterInRedux,
  setMultiRegisterIssuesStatusFilterInRedux,
  setMultiRegisterIssuesTaskOrIssueFilterInRedux,
} from "./multiRegisterIssuesSlice";

type TaskOrIssue = "ISSUE" | "TASK";

const MultiRegisterIssuesFilterBar: React.FC = () => {
  return (
    <Card mx="xxs" p="xs" withBorder radius="sm" bg="#fafafb" bd="1px solid var(--mantine-color-warmgray-2)">
      <Group justify="space-between" wrap="nowrap" gap="0">
        <Flex rowGap="xxs" columnGap="0.7em" wrap="wrap" align="center">
          <MultiRegisterTaskIssueToggle />
          <MultiRegisterFreeTextFilter />
          <MultiRegisterStatusFilter />
          <MultiRegisterAssignedToFilter />
        </Flex>
      </Group>
    </Card>
  );
};

const MultiRegisterTaskIssueToggle: React.FC = () => {
  const dispatch = useAppDispatch();
  const taskOrIssue = useAppSelector(selectMultiRegisterIssuesTaskOrIssueFilter);

  return (
    <SegmentedControl
      size="sm"
      color="mc-primary"
      value={taskOrIssue ?? "ALL"}
      data={[
        { label: "Alle", value: "ALL" },
        { label: "Saker", value: "ISSUE" },
        { label: "Aksjoner", value: "TASK" },
      ]}
      onChange={(value) =>
        dispatch(setMultiRegisterIssuesTaskOrIssueFilterInRedux(value === "ALL" ? null : (value as TaskOrIssue)))
      }
    />
  );
};

const MultiRegisterStatusFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const statusFilter = useAppSelector(selectMultiRegisterIssuesStatusFilter);
  const filter = useMemo(
    () => ({
      label: "Status",
      filterKey: IssueFieldEnum.STATUS,
      options: ISSUE_STATES_OPTIONS.map((status) => ({
        label: status.value,
        value: status.value,
        isActive: statusFilter.includes(status.value),
      })),
    }),
    [statusFilter],
  );

  return (
    <FilterDropdown
      filter={filter}
      value={statusFilter}
      onChange={(_, values) => dispatch(setMultiRegisterIssuesStatusFilterInRedux(values))}
      enableSearch
    />
  );
};

const MultiRegisterAssignedToFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const assignedToFilter = useAppSelector(selectMultiRegisterIssuesAssignedToFilter);
  const assignedToOptions = useAppSelector(selectMultiRegisterIssuesAssignedToFilterOptions);
  const filter = useMemo(
    () => ({
      label: "Ansvarlig",
      filterKey: IssueFieldEnum.ASSIGNED_TO_ID,
      options: assignedToOptions.map((option) => ({
        ...option,
        isActive: assignedToFilter.includes(option.value),
      })),
    }),
    [assignedToFilter, assignedToOptions],
  );

  return (
    <FilterDropdown
      filter={filter}
      value={assignedToFilter}
      onChange={(_, values) => dispatch(setMultiRegisterIssuesAssignedToFilterInRedux(values))}
      enableSearch
    />
  );
};

const MultiRegisterFreeTextFilter: React.FC = () => {
  const dispatch = useAppDispatch();
  const freeTextFilter = useAppSelector(selectMultiRegisterIssuesFreeTextFilter);
  const [value, setValue] = useState(freeTextFilter);
  const [debouncedValue] = useDebouncedValue(value, 400);

  useEffect(() => {
    setValue(freeTextFilter);
  }, [freeTextFilter]);

  useEffect(() => {
    dispatch(setMultiRegisterIssuesFreeTextFilterInRedux(debouncedValue));
  }, [debouncedValue, dispatch]);

  return (
    <TextInput
      placeholder="Søk..."
      value={value}
      onChange={(e) => setValue(e.currentTarget.value)}
      size="sm"
      w={180}
      miw={140}
      rightSection={value.length > 0 && <CloseButton size="sm" onClick={() => setValue("")} title="Tøm søk" />}
    />
  );
};

export default MultiRegisterIssuesFilterBar;
